import React from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import { Box, Button, TableRow, Typography } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { FormCheckbox } from '~/components/FormComponents/FormCheckbox.tsx';
import { FormAutocomplete } from '~/components/FormComponents/FormAutocomplete.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import {
  AVERAGE_BALANCE_MONTH_AMOUNT_FIELDS,
  AVERAGE_BALANCE_MONTHLY_TABLE_COLUMN_WIDTHS,
  AVERAGE_BALANCE_QUARTERLY_TABLE_COLUMN_WIDTHS,
  AVERAGE_BALANCE_SECTION_TITLE,
  AVERAGE_BALANCE_TABLE_ARIA_LABEL,
  AVERAGE_BALANCE_TABLE_BASE_HEADERS,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/constants.ts';
import { AverageBalanceTotals } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/AverageBalanceTotals.tsx';
import { FinancialConditionTable } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTable.tsx';
import { FinancialConditionTableCell } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTableCell.tsx';
import { FinancialConditionTableDeleteCell } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTableDeleteCell.tsx';
import type { TFinancialConditionFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import { FINANCIAL_CONDITION_FIELD_LABELS } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/constants.ts';
import {
  createBankAverageBalanceRow,
  filterBankOptions,
  getBankAverageBalanceTotals,
  getReportPeriodMonthNames,
  normalizeDecimalValue,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/utils.ts';
import { documentGenerationFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/styles.ts';
import { useDocumentGenerationOptions } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/hooks/useDocumentGenerationOptions.ts';

interface IAverageBalanceSectionProps {
  disabled: boolean;
}

export const AverageBalanceSection: React.FC<IAverageBalanceSectionProps> = ({ disabled }) => {
  const { control, watch } = useFormContext<TFinancialConditionFormValues>();
  const borrowerType = watch('borrowerType')?.value;
  const hasAverageBalanceRequirement = watch('hasAverageBalanceRequirement');
  const averageBalanceBanks = watch('averageBalanceBanks');
  const reportPeriod = watch('reportPeriod');
  const reportYear = watch('reportYear');
  const monthNames = getReportPeriodMonthNames(reportPeriod);
  const totals = getBankAverageBalanceTotals(averageBalanceBanks);
  const { fields, append, remove } = useFieldArray({ control, name: 'averageBalanceBanks' });
  const { bankOptions, currencyOptions, isBanksLoading, isCurrenciesLoading } =
    useDocumentGenerationOptions();

  if (!borrowerType) {
    return null;
  }

  return (
    <>
      <FormCheckbox
        name="hasAverageBalanceRequirement"
        label="Є зобов’язання щодо підтримання щомісячного середнього розміру залишків коштів"
        disabled={disabled}
        formControlLabelProps={{
          sx: (theme) => ({
            ...documentGenerationFormStyles.checkbox(theme),
            mt: theme.spacing(2),
          }),
        }}
      />

      {hasAverageBalanceRequirement && (
        <Box sx={documentGenerationFormStyles.section}>
          <Typography component="h3" sx={documentGenerationFormStyles.sectionTitle}>
            {AVERAGE_BALANCE_SECTION_TITLE}
          </Typography>
          <FinancialConditionTable
            ariaLabel={AVERAGE_BALANCE_TABLE_ARIA_LABEL}
            headers={[
              ...AVERAGE_BALANCE_TABLE_BASE_HEADERS,
              ...monthNames.map((month) => `${month} ${reportYear}`),
            ]}
            showActions={fields.length > 1}
            columnWidths={
              monthNames.length === 1
                ? [...AVERAGE_BALANCE_MONTHLY_TABLE_COLUMN_WIDTHS]
                : [...AVERAGE_BALANCE_QUARTERLY_TABLE_COLUMN_WIDTHS]
            }
          >
            {fields.map((field, index) => (
              <TableRow key={field.id}>
                <FinancialConditionTableCell>
                  <FormAutocomplete
                    name={`averageBalanceBanks.${index}.bankId`}
                    label={FINANCIAL_CONDITION_FIELD_LABELS.bank}
                    options={bankOptions}
                    loading={isBanksLoading}
                    filterOptions={filterBankOptions}
                    shouldSaveLabel
                    disabled={disabled}
                    fullWidth
                    sx={documentGenerationFormStyles.field}
                  />
                </FinancialConditionTableCell>
                <FinancialConditionTableCell>
                  <FormAutocomplete
                    name={`averageBalanceBanks.${index}.currency`}
                    label={FINANCIAL_CONDITION_FIELD_LABELS.currency}
                    options={currencyOptions}
                    loading={isCurrenciesLoading}
                    disabled={disabled}
                    fullWidth
                    sx={documentGenerationFormStyles.field}
                  />
                </FinancialConditionTableCell>
                {monthNames.map((month, monthIndex) => (
                  <FinancialConditionTableCell
                    key={AVERAGE_BALANCE_MONTH_AMOUNT_FIELDS[monthIndex]}
                  >
                    <FormInput
                      name={`averageBalanceBanks.${index}.${AVERAGE_BALANCE_MONTH_AMOUNT_FIELDS[monthIndex]}`}
                      label={month}
                      type="text"
                      inputProps={{ inputMode: 'decimal' }}
                      normalizeOnBlur={normalizeDecimalValue}
                      disabled={disabled}
                      fullWidth
                      sx={documentGenerationFormStyles.field}
                    />
                  </FinancialConditionTableCell>
                ))}
                <FinancialConditionTableDeleteCell
                  label={`Видалити банк ${index + 1}`}
                  disabled={disabled}
                  hidden={fields.length === 1}
                  onDelete={() => remove(index)}
                />
              </TableRow>
            ))}
          </FinancialConditionTable>

          <Button
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => append(createBankAverageBalanceRow())}
            disabled={disabled}
            sx={documentGenerationFormStyles.addButton}
          >
            Додати банк
          </Button>

          <AverageBalanceTotals totals={totals} monthNames={monthNames} />
        </Box>
      )}
    </>
  );
};
